import type { PasswordFieldLabels } from '@kilat/ui';

import type { I18n } from './use-i18n.js';

/**
 * Translated label bundles for the shared `@kilat/ui` form fields.
 *
 * `PasswordField` needs six strings that are not its label or placeholder (the
 * reveal toggle, its two accessible names, and four strength rungs). All three
 * signup funnels — operator, agent, recruiter — render the same field, so the
 * bundle is built once here rather than assembled at each call site.
 *
 * This exists because those strings used to be English defaults INSIDE the
 * shared component. Every caller omitted them, so a Malay, Chinese or Tamil
 * visitor reached the one form that turns a visitor into a customer and found
 * "Password", "Show" and "Too short" in English. Nothing caught it: the lint
 * rule that guards JSX text cannot see a default parameter value.
 *
 * Keeping the bundle in the app — not the package — is the point. `@kilat/ui`
 * has no locale of its own, and giving it one is how the bug happened.
 *
 * The strings themselves live under `common.password.*` because the agent and
 * dashboard sign-in screens render the same field. Three per-app copies of the
 * same six words is how two of them end up disagreeing in Tamil.
 */
export function passwordFieldLabels(t: I18n['t']): PasswordFieldLabels {
  return {
    show: t('common.password.show'),
    hide: t('common.password.hide'),
    showPasswordAria: t('common.password.showAria'),
    hidePasswordAria: t('common.password.hideAria'),
    strength: {
      tooShort: t('common.password.strengthTooShort'),
      gettingThere: t('common.password.strengthGettingThere'),
      solid: t('common.password.strengthSolid'),
      strong: t('common.password.strengthStrong'),
    },
  };
}
